<template>
  <eg-b-form class="essay-submission-form d-flex" @submit="onSubmit">
    <div class="essay-contents w-100">
      <!-- Class and Assignment Selection -->
      <eg-card
        v-if="showClassAssignmentSelectors"
        data-testid="submission-form-class-assignment"
        :label="$t('common.description')"
        onboarding-key="onboarding:clicked:submissionFormClassAssignmentQuestionIcon"
      >
        <div
          ref="classAndSelect"
          class="class-assignment-select-container mt-4 mb-0"
        >
          <div class="row">
            <div class="col-md-6">
              <eg-form-group :label="$t('common.class')" :required="true">
                <folder-class-selector
                  :value="selectedClass"
                  :classes="availableClasses"
                  :required="true"
                  :submitted="classSubmitted"
                  @input="onClassChange"
                />
              </eg-form-group>
            </div>
            <div class="col-md-6">
              <eg-form-group :label="$t('common.assignment')" :required="true">
                <folder-assignment-selector
                  :value="selectedAssignment"
                  :assignments="availableAssignments"
                  :selected-class="selectedClass"
                  :required="true"
                  :submitted="assignmentSubmitted"
                  @input="onAssignmentChange"
                />
              </eg-form-group>
            </div>
          </div>
          <p v-if="existingClassAndAssignment" class="mt-3 mb-0">
            {{ $t("common.alreadyCreated") }} {{ selectedClass.name }} →
            {{ selectedAssignment.name }}.
            <a href="#" @click="goToFolder">{{ $t("classes.clickHere") }}</a>
            {{ $t("common.toGradeMore") }}
          </p>
        </div>
      </eg-card>

      <eg-card
        v-show="!existingClassAndAssignment"
        data-testid="submission-form-rubric-select"
        :label="$t('common.rubric')"
        onboarding-key="onboarding:clicked:submissionFormRubricSelectQuestionIcon"
      >
        <template #extra-actions>
          <div class="d-flex justify-content-between align-items-center">
            <eg-button
              v-b-toggle.rubric-library-sidebar
              type="primary"
              class="ml-3"
            >
              {{ $t("rubrics.selectFromLibrary") }}
            </eg-button>
          </div>
        </template>
        <eg-rubric-card
          class="mt-4"
          :selected-rubric="selectedRubric"
          @delete-rubric="$emit('selectRubric', null)"
        />
      </eg-card>

      <div
        v-show="!existingClassAndAssignment"
        class="upload-area-container mb-3"
      >
        <reference-material-writing-prompt
          :selected-source-material="selectedSourceMaterial"
          @delete="deleteSourceMaterialHandler"
          @select-source-material="onSelectSourceMaterialHandler"
        />

        <eg-card
          class="mb-4"
          data-testid="submission-form-upload-area"
          :label="$t('common.uploadStudentWork')"
          onboarding-key="onboarding:clicked:submissionFormUploadAreaQuestionIcon"
        >
          <div class="paragraph-s text-neutral-secondary mb-4 mt-2">
            {{ $t("common.supportsSubmissions") }}
          </div>

          <eg-form-group
            v-if="!isFileUpload"
            id="name-input-group"
            ref="nameInput"
            :label="$t('common.name')"
            label-for="name-input"
            required
          >
            <eg-b-form-input
              id="name-input"
              v-model="name"
              type="text"
              :placeholder="$t('essays.enterEssayName')"
              :state="submitted ? Boolean(nameLength) : null"
            ></eg-b-form-input>
            <eg-b-form-invalid-feedback
              v-if="submitted && !Boolean(nameLength)"
            >
              {{ $t("essays.nameRequired") }}
            </eg-b-form-invalid-feedback>
          </eg-form-group>

          <upload-content-area
            ref="uploadArea"
            v-model="uploadType"
            class="upload-content-area mb-4"
            required
            :path="'essays'"
            :max-number-of-files="canBulkUpload ? 1000 : 1"
            :submitted="submitted"
            @file-added="files = $event"
            @file-removed="files = $event"
            @input-files="files = $event"
            @input-text="essayContents = $event"
          >
            <template #description>
              <div v-if="!canBulkUpload">
                <eg-b-button
                  variant="link"
                  class="px-0 py-0 account-link"
                  @click="trackAndRedirectToUpgrade('bulk_upload')"
                >
                  {{ $t("common.upgradeToPro") }}
                </eg-b-button>
                {{ $t("common.toUploadMoreEssays") }}
              </div>
            </template>
          </upload-content-area>
        </eg-card>

        <div v-if="submitting" class="upload-overlay">
          <eg-b-spinner small class="uploading-spinner"></eg-b-spinner>
          <span class="uploading-text">{{ $t("common.uploading") }}</span>
        </div>

        <grading-details-form
          v-model="gradingDetails"
          :rubric="selectedRubric"
          :source-material="selectedSourceMaterial"
        />
      </div>
    </div>

    <div class="sticky-button-container">
      <div class="sticky-button-wrapper">
        <eg-b-button
          v-show="!existingClassAndAssignment"
          type="submit"
          variant="info"
          :disabled="loading || submitting"
          class="float-right essay-submit-btn"
        >
          <template v-if="loading || submitting">
            <eg-b-spinner small></eg-b-spinner>
            {{ $t("common.uploading") }}
          </template>
          <template v-else>
            {{ $t("common.startGrading") }}
            <span>
              <img
                :src="require('@/assets/icons/arrow-right.svg')"
                alt="arrow-right"
              />
            </span>
          </template>
        </eg-b-button>
      </div>
    </div>
  </eg-b-form>
</template>

<script>
import trackAndRedirectToUpgrade from "@/mixins/trackAndRedirectToUpgrade";
import UploadContentArea from "./upload_content_area.vue";
import EgRubricCard from "@/components/rubrics/components/eg_rubric_card.vue";
import scrollToElement from "@/utils/scrollToEl.js";
import EgCard from "@/components/global/eg_card.vue";
import GradingDetailsForm from "../rubrics/gradingDetails/grading_details_form.vue";
import FolderClassSelector from "@/components/folders/folder_class_selector.vue";
import FolderAssignmentSelector from "@/components/folders/folder_assignment_selector.vue";
import EgFormGroup from "@/components/global/eg_form_group.vue";
import { addFolder } from "@/api";
import { PLATFORMS, LSK_SUGGESTION_FORM_SELECTED_RUBRIC } from "@/constants";
import isLMSMixin from "@/mixins/isLMS";
import ReferenceMaterialWritingPrompt from "@/components/source-materials/reference_material_writing_prompt";

const getAdditionalPayload = ({ name, content, fileUrl }) => {
  const additionalPayload = {
    name,
    content: content || "",
  };

  if (fileUrl) {
    additionalPayload.fileUrl = fileUrl;
  }

  return additionalPayload;
};

const defaultData = {
  uploadType: "files",
  files: [],
  essayContents: "",
  name: "",
};

export default {
  name: "SubmissionForm",
  components: {
    ReferenceMaterialWritingPrompt,
    EgCard,
    GradingDetailsForm,
    UploadContentArea,
    EgRubricCard,
    FolderClassSelector,
    FolderAssignmentSelector,
    EgFormGroup,
  },
  mixins: [
    trackAndRedirectToUpgrade,
    // this.isLMS
    isLMSMixin,
  ],

  inject: ["workspacesProvider", "scrollContentToTop"],

  props: {
    selectedRubric: {
      type: Object,
      default: () => {},
    },
    forceExit: {
      type: Object,
      default: () => {},
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["selectRubric", "essaySubmitted"],
  data() {
    // TODO: check the correct prepopulation from this variable
    // VUE_APP_FREE_TOOLS_DEFAULT_RUBRIC_ID

    return {
      folderId: null,
      rubricId: null,
      gradingDetails: {},
      selectedSourceMaterial: null,
      files: [],
      essayContents: "",
      name: null,
      uploadType: "files",
      loading: false,
      submitted: false,
      classSubmitted: false,
      assignmentSubmitted: false,
      selectedClass: null,
      selectedAssignment: null,
      isGradingDetailsChanged: false,
      initData: {
        gradingDetails: {},
      },
    };
  },

  computed: {
    nameLength() {
      return this.name?.trim().length;
    },

    isDirty() {
      return (
        !!this.name ||
        !!this.essayContents ||
        !!this.selectedClass ||
        !!this.selectedAssignment ||
        !!this.selectedSourceMaterial ||
        this.files.length > 0 ||
        this.isGradingDetailsChanged
      );
    },

    isFileUpload() {
      return this.uploadType === "files";
    },

    canBulkUpload() {
      // TODO: Temporarily allow everyone to use bulk upload.
      // After 3.0 release, if we still allow all users to bulk upload, remove this code.
      return true;
      // return this.$store.getters.userIsNotPausedAndPlanMoreThenPro;
    },

    showClassAssignmentSelectors() {
      // Show selectors when we're on the manual grading route (not in workspace context)
      return this.$route.name === "manual-grading";
    },

    extractedData() {
      if (!this.workspacesProvider) {
        return { classes: [], assignments: [] };
      }
      return this.workspacesProvider.extractClassesAndAssignments();
    },

    availableClasses() {
      if (!this.showClassAssignmentSelectors || !this.workspacesProvider) {
        return [];
      }
      return this.extractedData.classes;
    },

    availableAssignments() {
      if (
        !this.showClassAssignmentSelectors ||
        !this.workspacesProvider ||
        !this.selectedClass?.id
      ) {
        return [];
      }

      const { assignments } =
        this.workspacesProvider.extractClassesAndAssignments(
          this.selectedClass.children
        );

      return assignments;
    },

    existingClassAndAssignment() {
      if (!!this.selectedClass?.id && !!this.selectedAssignment?.id) {
        if (this.selectedAssignment.id) {
          const folder = this.workspacesProvider.getFolderById(
            PLATFORMS.ESSAYGRADER,
            this.selectedAssignment.id
          );

          if (!folder?.hasGradingDetails) {
            return false;
          }
        }

        return true;
      }

      return false;
    },
  },
  watch: {
    selectedRubric() {
      if (this?.selectedRubric?.intensity) {
        this.gradingDetails.intensity = this.selectedRubric.intensity;
      }

      if (this?.selectedRubric?.gradeLevel) {
        this.gradingDetails.level = this.selectedRubric.gradeLevel;
      }

      this.isGradingDetailsChanged = true;
    },

    uploadType() {
      this.submitted = false;
    },

    "$route.params.id": {
      handler() {
        this.setInitData();
      },
    },

    gradingDetails: {
      handler(newValue) {
        const gradingDetails = this.initData.gradingDetails;

        this.isGradingDetailsChanged =
          newValue.inputLanguageId !== gradingDetails.inputLanguageId ||
          newValue.intensity !== gradingDetails.intensity ||
          newValue.length !== gradingDetails.length ||
          newValue.level !== gradingDetails.level ||
          newValue.outputLanguageId !== gradingDetails.outputLanguageId ||
          newValue.flexibleScoring !== gradingDetails.flexibleScoring ||
          newValue.customInstructions !== gradingDetails.customInstructions ||
          newValue.rubricId !== gradingDetails.rubricId;
      },
      deep: true,
    },
  },

  mounted() {
    this.initData.gradingDetails = JSON.parse(
      JSON.stringify(this.gradingDetails)
    );
  },

  methods: {
    scrollToTop() {
      this.scrollContentToTop?.();
    },

    goToFolder(e) {
      const folderId = this.selectedAssignment.id;
      this.$emit("forceExit");
      this.resetClassAndAssignment(e);
      this.$router.push({
        name: "workspace",
        params: {
          workspaceKey: PLATFORMS.ESSAYGRADER,
          id: folderId,
        },
      });
    },
    resetClassAndAssignment(e) {
      e.preventDefault();
      this.selectedClass = null;
      this.selectedAssignment = null;
    },
    getCorrectValue(selectedValue, objects) {
      const name = selectedValue?.name?.trim();
      const existingObject = objects.find((item) => item.name === name);

      if (existingObject) {
        return existingObject;
      } else {
        return {
          ...selectedValue,
          name,
        };
      }
    },

    onClassChange(selectedClass) {
      this.selectedClass = this.getCorrectValue(
        selectedClass,
        this.availableClasses
      );
      this.selectedAssignment = undefined;
    },
    onAssignmentChange(selectedAssignment) {
      this.selectedAssignment = this.getCorrectValue(
        selectedAssignment,
        this.availableAssignments
      );
    },
    onSelectSourceMaterialHandler(data) {
      this.selectedSourceMaterial = data;
      this.gradingDetails.sourceMaterialId = data.id;
    },
    deleteSourceMaterialHandler() {
      this.selectedSourceMaterial = undefined;
      this.gradingDetails.sourceMaterialId = undefined;
    },
    rubricGroupFunction(rubrics) {
      return [
        { id: "my", title: this.$t("rubrics.myRubrics"), items: rubrics },
        {
          id: "platform",
          title: this.$t("rubrics.platformRubrics"),
          items: this.platformRubrics,
        },
      ];
    },

    resetForm() {
      this.files = [];
      this.submitted = false; // Reset validation state
      this.classSubmitted = false;
      this.assignmentSubmitted = false;
      localStorage.removeItem(LSK_SUGGESTION_FORM_SELECTED_RUBRIC);
    },

    showErrorToast(message) {
      this.$showToastError(message);
    },

    validateForm() {
      const rules = [
        {
          condition: this.isFileUpload && this.files.length === 0,
          ref: this.$refs.uploadArea,
          errorMessage: this.$t("essays.uploadEssay"),
        },
        {
          condition: !this.isFileUpload && !this.nameLength,
          ref: this.$refs.nameInput,
          errorMessage: this.$t("essays.enterName"),
        },
        {
          condition:
            !this.isFileUpload && this.essayContents.trim().length === 0,
          ref: this.$refs.uploadArea,
          errorMessage: this.$t("essays.enterNameAndText"),
        },
      ];

      // For manual grading, validate class/assignment first
      if (this.showClassAssignmentSelectors) {
        // Check if class is provided (either existing with id or custom with name)
        const hasValidClass =
          this.selectedClass &&
          (this.selectedClass.id || this.selectedClass.name);

        // Check if assignment is provided (either existing with id or custom with name)
        const hasValidAssignment =
          this.selectedAssignment &&
          (this.selectedAssignment.id || this.selectedAssignment.name);

        rules.unshift(
          {
            condition: !hasValidClass,
            ref: this.$refs.classAndSelect,
            errorMessage: this.$t("essays.selectClass"),
          },
          {
            condition: !hasValidAssignment,
            ref: this.$refs.classAndSelect,
            errorMessage: this.$t("essays.selectAssignment"),
          }
        );
      }

      // Reset all submitted flags first
      this.submitted = false;
      this.classSubmitted = false;
      this.assignmentSubmitted = false;

      for (const rule of rules) {
        if (rule.condition) {
          // Set specific submitted flags based on error type
          if (rule.errorMessage.includes("class")) {
            this.classSubmitted = true;
          } else if (rule.errorMessage.includes("assignment")) {
            this.assignmentSubmitted = true;
          } else {
            this.submitted = true;
          }

          scrollToElement(rule.ref);
          this.showErrorToast(`Error: ${rule.errorMessage}`);

          return false;
        }
      }

      return true;
    },

    // handles folders creation from class and assignments input
    async findOrCreateClassAssignmentFolders() {
      if (this.$route.params.id) {
        return { folderId: +this.$route.params.id };
      }

      // if selected existing assignment folder
      if (this.selectedAssignment?.id) {
        return { folderId: this.selectedAssignment?.id };
      }

      // if selected existing class folder, but no assignment
      if (this.selectedClass?.id) {
        const response = await addFolder({
          folders: [
            {
              name: this.selectedAssignment.name,
              parentId: this.selectedClass.id,
            },
          ],
        });

        return {
          folderId: response.data[0].id,
          createdFolder: response.data[0],
        };
      }

      // if neither class nor assignment folders exist yet
      const response = await addFolder({
        folders: [
          {
            name: this.selectedClass.name,
            children: [this.selectedAssignment],
          },
        ],
      });

      return {
        folderId: response.data[0].children[0].id,
        createdFolder: response.data[0],
      };
    },

    async onSubmit(e) {
      e.preventDefault();

      if (this.existingClassAndAssignment) {
        return;
      }

      if (!this.selectedRubric?.id) {
        return this.showErrorToast(this.$t("essays.selectRubric"));
      }

      // Validate form first (including class/assignment for manual grading)
      const isValid = this.validateForm();

      if (!isValid) {
        return;
      }

      // Upload files for all cases (manual and non-manual grading)
      try {
        this.loading = true;
        await this.$refs.uploadArea.upload();
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.showErrorToast(error.response?.data?.error);
        return;
      }

      const { folderId, createdFolder } =
        await this.findOrCreateClassAssignmentFolders();
      const submitPayload = {
        folderId,
        createdFolder,
        gradingDetails: this.gradingDetails,
      };

      // uploading bulk essay files
      if (this.files.length > 1) {
        try {
          this.loading = true;

          const filesPayload = this.files.map((file) => {
            return getAdditionalPayload(file);
          });

          submitPayload.files = filesPayload;

          this.$emit("essaySubmitted", submitPayload);

          this.loading = false;
        } catch (error) {
          this.loading = false;
          this.showErrorToast(error.response?.data?.error);
        }
      }

      // uploading single essay file
      if (this.files.length === 1) {
        try {
          this.loading = true;

          const filePayload = getAdditionalPayload(this.files[0]);

          this.loading = false;

          submitPayload.files = [filePayload];

          this.$emit("essaySubmitted", submitPayload);
        } catch (error) {
          this.loading = false;
          this.showErrorToast(error.response?.data?.error);
        }
      }

      // uploading plain text essay
      if (this.essayContents) {
        submitPayload.files = [
          {
            name: this.name,
            content: this.essayContents,
          },
        ];

        this.$emit("essaySubmitted", submitPayload);
      }

      this.resetForm();
    },
    inputDefaultHandler({ type, value }) {
      this[type] = value;
    },

    setInitData() {
      const { uploadType, files, essayContents, name } = defaultData;

      this.uploadType = uploadType;
      this.files = files;
      this.essayContents = essayContents;
      this.name = name;
      this.isGradingDetailsChanged = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.account-link {
  font-size: 12.8px;
  border: none;
  vertical-align: baseline;
}

.essay-contents {
  display: flex;
  flex-direction: column;
  gap: 24px;
  // This is the height of the sticky button container
  margin-bottom: 71px;
}

.essay-submission-form::v-deep {
  .active .text-muted {
    color: white !important;
  }
}

.rubric-name {
  color: var(--Text-Body, #212529);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.rubric-created-by {
  color: var(--Text-Body, #212529);
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
}

.upload-area-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  ::v-deep .additional-collapse .btn-link {
    color: var(--black-700);
  }
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7); /* Slightly transparent overlay */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10; /* Ensure it appears above other content */
}

.uploading-spinner {
  color: #532cb5;
  margin-right: 5px;
}

.uploading-text {
  color: #532cb5;
  font-weight: bold;
}
.class-assignment-select-container {
  margin-bottom: 16px;
  .form-group {
    margin: 0;
  }
}
.additional-collapse ::v-deep .additional-collapse-control {
  font-size: 18px;
  font-family: Inter;
  font-weight: 700;
  margin-bottom: 0 !important;
}

.sticky-button-container {
  display: flex;
  justify-content: flex-end;
  width: calc(100% - var(--sidebar-width, 264px));
  position: fixed;
  bottom: 0;
  right: 0;
  background-color: white;
  padding: 16px 24px;
  z-index: 4;
  --sticky-button-padding-left: 70px;
  border-top: 1px solid var(--color-stroke-neutral-default);

  .sticky-button-wrapper {
    width: 100%;
    max-width: calc(960px + var(--sticky-button-padding-left) * 2);
    margin: auto;
  }

  .essay-submit-btn {
    margin-right: var(--sticky-button-padding-left);
  }
}
</style>

<style lang="scss">
.essay-submission-form {
  .form-control.is-invalid:focus {
    border-color: none;
    box-shadow: none;
  }
  .form-control.is-valid:focus {
    border-color: none;
    box-shadow: none;
  }
  .button-content.is-invalid {
    border: 1px red solid;
  }

  // Custom width for class and assignment dropdowns
  .class-assignment-select-container {
    .dropdown-menu {
      width: 100% !important;
      min-width: 100% !important;
    }

    // Transparent background for create buttons
    .create-item.dropdown-item-wrapper.sticky {
      background-color: transparent;
    }
    .search-input {
      color: var(--color-text-neutral-on-white);
    }
  }
}
</style>
